import { css } from "@emotion/css";
import { Button, ErrorPanel, DeprecatedIconButton, Switch, Tooltip } from "@octopusdeploy/design-system-components";
import { ArrowExpandIcon, CodeIcon, ConnectionIcon, DuplicateIcon, KubernetesIcon } from "@octopusdeploy/design-system-icons";
import { borderRadius, borderWidth, space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { TimelineItem, KubernetesLiveManifestDiff } from "@octopusdeploy/octopus-server-client";
import { useState, useRef, type ReactNode, useMemo } from "react";
import type { ViewKubernetesLiveManifestEvent } from "~/analytics/Analytics";
import { useAnalyticLiveStatusManifestDispatch } from "~/analytics/Analytics";
import { ManifestDiffHeader } from "~/areas/projects/components/Observability/ObjectDrawer/Tabs/ManifestDiffHeader";
import type { LiveStatusObjectDrawerLoadingTabProps } from "~/areas/projects/components/Observability/ObjectDrawer/Tabs/TabProps";
import { DiffConfigMenu, type DiffSettings } from "~/areas/tasks/components/Task/Kubernetes/DiffConfigMenu";
import { CodeViewerWithLoading } from "~/areas/tasks/components/Task/Kubernetes/components/CodeViewerWithLoading/CodeViewerWithLoading";
import { createErrorInfo } from "~/areas/tasks/components/Task/Kubernetes/createErrorInfo";
import type { CodeComponent } from "~/components/Code/Code";
import { DiffViewer, type DiffViewType } from "~/components/DiffViewer/DiffViewer";
import useLocalStorage from "~/hooks/useLocalStorage";
import DateFormatter from "~/utils/DateFormatter/index";
type ManifestTabProps = LiveStatusObjectDrawerLoadingTabProps & {
    liveManifest: string | null;
    desiredManifest: string | null;
    diff: KubernetesLiveManifestDiff | null;
    latestDeployment: TimelineItem;
    analyticEventData: Omit<ViewKubernetesLiveManifestEvent, "type" | "diffViewType">;
};
const styles = {
    manifestUpdatedDesc: css({
        display: "flex",
        flexDirection: "row",
        gap: space[8],
        font: text.body.regular.medium,
        color: themeTokens.color.text.secondary,
        alignItems: "center",
    }),
    codeViewerHeader: css({
        display: "flex",
        flexDirection: "row",
        justifyContent: "flex-end",
        gap: space[16],
        border: `${borderWidth[1]} solid ${themeTokens.color.border.primary}`,
        padding: `${space[8]} ${space[12]}`,
        borderTopRightRadius: borderRadius["small"],
        borderTopLeftRadius: borderRadius["small"],
    }),
    diffViewerContainer: css({
        position: "relative",
        border: `${borderWidth[1]} solid ${themeTokens.color.border.primary}`,
        marginTop: space[16],
        borderTop: 0,
        borderRadius: borderRadius["small"],
        //this is required for the outer border radius to properly clip content
        overflow: "auto",
    }),
    codeViewerContainer: css({
        position: "relative",
        border: `${borderWidth[1]} solid ${themeTokens.color.border.primary}`,
        borderTop: 0,
        borderBottomRightRadius: borderRadius["small"],
        borderBottomLeftRadius: borderRadius["small"],
        //this is required for the outer border radius to properly clip content
        overflow: "auto",
    }),
    toggleButtonContainer: css({
        display: "flex",
        flexDirection: "row",
        padding: space[4],
        background: themeTokens.color.background.tertiary,
        borderRadius: borderRadius.medium,
        gap: space[4],
    }),
    diffActionsContainer: css({
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
    }),
    actionsContainer: css({
        display: "flex",
        flexDirection: "row",
        gap: space[16],
        alignItems: "center",
    }),
    headerContainer: css({
        display: "flex",
        flexDirection: "row",
        paddingBottom: space[16],
        justifyContent: "space-between",
        alignItems: "center",
    }),
};
export const ManifestTab = (props: ManifestTabProps) => {
    const { detailedResource, liveManifest, desiredManifest, diff, isLoading, error, analyticEventData } = props;
    const [showLiveManifest, setShowLiveManifest] = useState<boolean>(true);
    const [showDiff, setShowDiff] = useState<boolean>(false);
    const [diffViewTypeInStorage, setDiffViewTypeInStorage] = useLocalStorage<DiffViewType>("Octopus.Kubernetes.ManifestDiffs.DiffViewType", "split");
    const [diffSettings, setDiffSettings] = useState<DiffSettings>({ viewType: diffViewTypeInStorage, hideUnchangedManifests: false });
    const dispatch = useAnalyticLiveStatusManifestDispatch();
    const codeRef = useRef<CodeComponent>(null);
    const manifestErrorInfo = createErrorInfo(error);
    const viewToggleButtons = useMemo(() => {
        // conditionally wraps the child component in a tooltip
        const wrapInTooltip = (children: ReactNode, tooltipText: string, wrap: boolean) => (wrap ? <Tooltip content={tooltipText}>{children}</Tooltip> : children);
        const updateDiffSettings = (updated: Partial<DiffSettings>) => {
            setDiffSettings((prev) => {
                const updatedSettings = {
                    ...prev,
                    ...updated,
                };
                setDiffViewTypeInStorage(updatedSettings.viewType);
                return updatedSettings;
            });
        };
        return (<div className={styles.actionsContainer}>
                {wrapInTooltip(<div className={styles.diffActionsContainer}>
                        <Switch value={showDiff} onChange={() => {
                    setShowDiff((prev) => !prev);
                    setShowLiveManifest(true);
                    dispatch("View Live Manifest Diff in Drawer", { ...analyticEventData, type: "diff", diffViewType: diffSettings.viewType });
                }} disabled={diff === null} label="Show diff"/>
                        <DiffConfigMenu updateDiffSettings={updateDiffSettings} diffSettings={diffSettings} hideUnchangedManifestOption={true} disabled={diff === null}/>
                    </div>, "Diff is not available for this resource", diff === null)}
                <div className={styles.toggleButtonContainer}>
                    {wrapInTooltip(<Button label={"Applied Manifest"} icon={<CodeIcon size={20}/>} importance={showLiveManifest ? "quiet" : "loud"} onClick={() => {
                    setShowLiveManifest(false);
                    setShowDiff(false);
                    dispatch("View Applied Manifest in Drawer", { ...analyticEventData, type: "applied", diffViewType: diffSettings.viewType });
                }} disabled={!desiredManifest}/>, "An applied manifest is not available for this resource", !desiredManifest)}
                    <Button label={"Live Manifest"} icon={<KubernetesIcon size={20}/>} importance={showLiveManifest ? "loud" : "quiet"} onClick={() => {
                setShowLiveManifest(true);
                dispatch("View Live Manifest in Drawer", { ...analyticEventData, type: "live", diffViewType: diffSettings.viewType });
            }} disabled={!liveManifest}/>
                </div>
            </div>);
    }, [showDiff, diff, diffSettings, showLiveManifest, desiredManifest, liveManifest, setDiffViewTypeInStorage, dispatch, analyticEventData]);
    return (<div>
            {manifestErrorInfo && <ErrorPanel error={manifestErrorInfo}/>}
            {!manifestErrorInfo && (<>
                    <div className={styles.headerContainer}>
                        <div className={styles.manifestUpdatedDesc}>
                            <ConnectionIcon size={16}/>
                            <span>
                                Live manifest last updated on <span title={DateFormatter.dateToLongFormat(detailedResource?.LastUpdated) || undefined}>{DateFormatter.dateToShortFormat(detailedResource?.LastUpdated)}</span>
                            </span>
                        </div>
                        {viewToggleButtons}
                    </div>
                    {showDiff && diff ? (<>
                            <ManifestDiffHeader latestDeployment={props.latestDeployment} liveStatusResource={props.detailedResource}></ManifestDiffHeader>
                            <div className={styles.diffViewerContainer}>
                                <DiffViewer originalText={diff.Left} updatedText={diff.Right} unifiedDiff={diff.Diff} diffType={diffSettings.viewType}/>
                            </div>
                        </>) : (<>
                            <div className={styles.codeViewerHeader}>
                                <Tooltip content={"Copy to clipboard"}>
                                    <DeprecatedIconButton customIcon={<DuplicateIcon size={20} color={themeTokens.color.icon.primary}/>} onClick={async () => await codeRef.current?.copyToClipboard()}/>
                                </Tooltip>
                                <Tooltip content={"Enter full screen"}>
                                    <DeprecatedIconButton customIcon={<ArrowExpandIcon size={20} color={themeTokens.color.icon.primary}/>} onClick={() => codeRef.current?.showInFullScreen()}/>
                                </Tooltip>
                            </div>
                            <div className={styles.codeViewerContainer}>
                                <CodeViewerWithLoading ref={codeRef} language={"YAML"} value={showLiveManifest ? liveManifest?.trimEnd() : desiredManifest?.trimEnd()} showLineNumbers={true} lineWrapping={true} isLoading={isLoading}/>
                            </div>
                        </>)}
                </>)}
        </div>);
};
